import React, { useEffect, useState } from 'react';
import { ArrowLeft, ChevronLeft, ChevronRight, ChevronDown, ListOrdered } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import DocsBreadcrumb from './DocsBreadcrumb';
import KeyboardShortcutsOverlay from './KeyboardShortcutsOverlay';
import BackToTop from './BackToTop';

const BG_IMAGE = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/adx2h6g0_TRY%20THIS.png';

export const APPENDIX_CONTAINERS = [
  { id: 'error-codes',    letter: 'A', title: 'Error Codes',    link: '/technical-documentation/appendix/error-codes' },
  { id: 'url-encoding',   letter: 'B', title: 'URL Encoding',   link: '/technical-documentation/appendix/url-encoding' },
  { id: 'country-codes',  letter: 'C', title: 'Country Codes',  link: '/technical-documentation/appendix/country-codes' },
  { id: 'currency-codes', letter: 'D', title: 'Currency Codes', link: '/technical-documentation/appendix/currency-codes' },
  { id: 'test-cards',     letter: 'E', title: 'Test Cards',     link: '/technical-documentation/appendix/test-cards' },
];

const Pagination = ({ currentId }) => {
  const idx = APPENDIX_CONTAINERS.findIndex((c) => c.id === currentId);
  const prev = idx > 0 ? APPENDIX_CONTAINERS[idx - 1] : null;
  const next = idx < APPENDIX_CONTAINERS.length - 1 ? APPENDIX_CONTAINERS[idx + 1] : null;

  const Card = ({ item, side }) => {
    if (!item) return <div className="flex-1" />;
    const isPrev = side === 'prev';
    return (
      <Link
        to={item.link}
        className={`group flex-1 flex items-center gap-3 rounded-lg border border-gray-200 bg-white/90 hover:bg-white hover:border-[#1F4E79] hover:shadow-md p-4 transition-all duration-200 ${isPrev ? '' : 'flex-row-reverse text-right'}`}
        data-testid={`appendix-pagination-${side}`}
      >
        {isPrev ? <ChevronLeft size={20} className="text-[#1F4E79] flex-shrink-0" />
                : <ChevronRight size={20} className="text-[#1F4E79] flex-shrink-0" />}
        <div>
          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em]">{isPrev ? 'Previous' : 'Next'}</p>
          <p className="font-bold text-[#1F4E79] group-hover:text-[#163a5f] text-sm">{item.title}</p>
        </div>
      </Link>
    );
  };

  return (
    <nav className="flex flex-col sm:flex-row gap-4 mt-8" data-testid="appendix-pagination">
      <Card item={prev} side="prev" />
      <Card item={next} side="next" />
    </nav>
  );
};

const AppendixPageLayout = ({
  currentId,
  title,
  subtitle,
  titleBadge,
  toc = [],
  mainTestId = 'appendix-container-page',
  sidebarTestId = 'appendix-container-sidebar',
  children,
}) => {
  const navigate = useNavigate();
  const flatIds = toc.flatMap((i) => [i.id, ...(i.children?.map((c) => c.id) || [])]);
  const [activeId, setActiveId] = useState(flatIds[0] || '');
  const current = APPENDIX_CONTAINERS.find((c) => c.id === currentId);

  useEffect(() => {
    if (flatIds.length === 0) return undefined;
    const handleScroll = () => {
      const offset = 140;
      let active = flatIds[0];
      for (const id of flatIds) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top - offset <= 0) active = id;
      }
      setActiveId(active);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNavClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
    setMobileTocOpen(false);
  };

  const [mobileTocOpen, setMobileTocOpen] = useState(false);

  const renderTocLinks = (idPrefix = '') =>
    toc.map((item) => (
      <a
        key={item.id}
        href={`#${item.id}`}
        onClick={(e) => handleNavClick(e, item.id)}
        className={`block px-4 py-2 text-sm transition-colors duration-150 border-l-2 ${
          activeId === item.id
            ? 'border-[#1F4E79] bg-blue-50 text-[#1F4E79] font-semibold'
            : 'border-transparent text-gray-700 hover:bg-gray-50 hover:text-[#1F4E79]'
        }`}
        data-testid={`${idPrefix}toc-link-${item.id}`}
      >
        {item.label}
      </a>
    ));

  return (
    <div className="min-h-screen flex flex-col scroll-smooth" data-testid={mainTestId}>
      <Header />
      <div className="h-20" />
      <div className="relative flex-1">
        <div className="fixed inset-0 top-20 bg-cover bg-center" style={{ backgroundImage: `url(${BG_IMAGE})` }} />

        <div className="relative z-10 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10">
          <DocsBreadcrumb section="appendix" currentId={currentId} />
          <button
            onClick={() => navigate('/technical-documentation/appendix')}
            className="flex items-center gap-2 text-[#1F4E79] hover:text-[#163a5f] font-medium mb-8 transition-colors duration-200"
            data-testid={`${mainTestId}-back-button`}
          >
            <ArrowLeft size={18} />
            Back to Appendix
          </button>

          <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_256px] lg:gap-8">
            <main className="min-w-0">
              {/* Mobile collapsed TOC pill */}
              {toc.length > 0 && (
                <div className="lg:hidden mb-6 border border-gray-200 rounded-xl overflow-hidden bg-white/90 backdrop-blur-sm">
                  <button
                    type="button"
                    onClick={() => setMobileTocOpen((o) => !o)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 text-left"
                    aria-expanded={mobileTocOpen}
                    data-testid="mobile-toc-toggle"
                  >
                    <span className="flex items-center gap-2 text-sm font-semibold text-[#0a1929]">
                      <ListOrdered size={16} className="text-[#ef5b25]" />
                      On this page
                      <span className="text-gray-400 font-normal">({toc.length})</span>
                    </span>
                    <ChevronDown
                      size={18}
                      className={`text-gray-500 transition-transform ${mobileTocOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {mobileTocOpen && (
                    <nav className="bg-white max-h-[60vh] overflow-y-auto py-2" data-testid="mobile-toc-panel">
                      {renderTocLinks('m-')}
                    </nav>
                  )}
                </div>
              )}

              <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-12 mb-8">
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  <h1 className="text-4xl sm:text-5xl font-bold text-gray-900" data-testid={`${mainTestId}-title`}>{title}</h1>
                  {titleBadge && (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-bold bg-[#1F4E79] text-white uppercase tracking-wide">
                      {titleBadge}
                    </span>
                  )}
                </div>
                {subtitle && <p className="text-gray-600 text-lg">{subtitle}</p>}
              </div>

              {children}

              <Pagination currentId={currentId} />
            </main>

            {toc.length > 0 && (
              <aside
                className="hidden lg:block lg:sticky lg:top-28 self-start rounded-lg shadow-md border border-gray-200 bg-white/85 backdrop-blur-sm overflow-hidden"
                data-testid={sidebarTestId}
              >
                <div className="px-4 py-3 bg-[#1F4E79] text-white">
                  <p className="text-sm font-semibold tracking-wide">On This Page</p>
                </div>
                <nav className="py-2 max-h-[calc(100vh-10rem)] overflow-y-auto">{renderTocLinks()}</nav>
              </aside>
            )}
          </div>
        </div>
      </div>

      <div className="relative z-10"><Footer /></div>
      <KeyboardShortcutsOverlay />
      <BackToTop />
    </div>
  );
};

export default AppendixPageLayout;
